import { useCallback, useEffect, useState } from 'react';
import { Button } from '../../components/Button';
import { ApiError } from '../../services/api';
import { getMyInvoices } from '../../services/billing';
import { NO_INVOICES, type MyInvoice, type MyInvoices } from '../../types/billing';
import {
  DISPLAY_STATUS_LABEL,
  dateOnlyCell,
  formatMoney,
} from '../admin/billing/billingFormat';
import './MyBillingPage.css';

const describe = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? err.message : fallback;

/**
 * What an invoice is called on the row, with a fallback that is honest rather than blank.
 *
 * A Stripe invoice has no number until it is finalized, and inventing one would give a customer a
 * reference their own finance office cannot find at Stripe.
 */
const reference = (invoice: MyInvoice): string =>
  invoice.number ?? `Issued ${dateOnlyCell(invoice.issuedOn)}`;

/**
 * The organization's own billing page: what it owes, every invoice it has had, and where to pay.
 *
 * **Separate from the staff screens on purpose.** `/admin/billing` is system admin only and shows
 * the row this system keeps about a customer: the plan, the contacts, the reminder ledger, the
 * Stripe wiring. This shows a customer their own bill and nothing else, from an endpoint that takes
 * no organization id at all — the server reads the caller's claim, so there is nothing here for a
 * client to pass and nothing to get wrong.
 *
 * **A Pay button appears only where there is somewhere to pay.** The API sends a link for an
 * invoice Stripe holds and null for one raised here, which is collected by cheque, wire or ACH
 * against an accounts payable department. A button that led nowhere would be worse than no button.
 */
export const MyBillingPage = () => {
  const [billing, setBilling] = useState<MyInvoices>(NO_INVOICES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const load = useCallback((signal: AbortSignal) => {
    setLoading(true);
    setError(null);

    getMyInvoices(signal)
      .then((loaded) => {
        if (signal.aborted) return;
        setBilling(loaded);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setError(describe(err, 'Could not load your billing.'));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, reloadToken]);

  const owed = billing.outstandingCents;

  return (
    <section className="my-billing" aria-labelledby="my-billing-title">
      <header className="my-billing__header">
        <h2 className="my-billing__title" id="my-billing-title">
          Billing
        </h2>
        <p className="my-billing__subtitle">
          What your organization has been charged, and what is still owed.
        </p>
      </header>

      {error && (
        <div className="my-billing__banner my-billing__banner--error" role="alert">
          <span>{error}</span>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setReloadToken((token) => token + 1)}
            data-testid="my-billing-retry"
          >
            Try again
          </Button>
        </div>
      )}

      {loading ? (
        <p className="my-billing__message">Loading your billing...</p>
      ) : (
        <>
          {/*
            The outstanding total leads, because it is the only figure most people open this page
            for. It is what is still owed across every open invoice, not the sum of what has ever
            been billed.
          */}
          <div className="my-billing__owed" data-testid="my-billing-owed">
            <span className="my-billing__owed-label">Currently owed</span>
            <span className="my-billing__owed-value">
              {formatMoney(owed, billing.currency)}
            </span>
            {owed === 0 && billing.items.length > 0 && (
              <span className="my-billing__owed-note">Nothing outstanding. Thank you.</span>
            )}
          </div>

          {billing.items.length === 0 ? (
            <p className="my-billing__message" data-testid="my-billing-empty">
              Nothing has been billed to your organization yet.
            </p>
          ) : (
            <div className="my-billing__table-wrap">
              <table className="my-billing__table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Issued</th>
                    <th>Due</th>
                    <th>Amount</th>
                    <th>Owed</th>
                    <th>Status</th>
                    <th>
                      <span className="my-billing__sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {billing.items.map((invoice) => (
                    <tr key={invoice.id} data-testid={`my-billing-row-${invoice.id}`}>
                      <td>{reference(invoice)}</td>
                      <td>{dateOnlyCell(invoice.issuedOn)}</td>
                      <td>{dateOnlyCell(invoice.dueOn)}</td>
                      <td>{formatMoney(invoice.amountCents, invoice.currency)}</td>
                      <td>
                        {invoice.amountRemainingCents > 0
                          ? formatMoney(invoice.amountRemainingCents, invoice.currency)
                          : '-'}
                      </td>
                      <td>
                        <span
                          className={`my-billing__status my-billing__status--${invoice.displayStatus}`}
                        >
                          {DISPLAY_STATUS_LABEL[invoice.displayStatus]}
                        </span>
                        {invoice.displayStatus === 'overdue' && invoice.daysOverdue > 0 && (
                          <span className="my-billing__overdue-days">
                            {' '}
                            {invoice.daysOverdue}d
                          </span>
                        )}
                      </td>
                      <td className="my-billing__row-actions">
                        {invoice.payUrl && (
                          <a
                            className="my-billing__pay"
                            href={invoice.payUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`my-billing-pay-${invoice.id}`}
                          >
                            {invoice.amountRemainingCents > 0 ? 'Pay' : 'View at Stripe'}
                          </a>
                        )}
                        {invoice.pdfUrl && (
                          <a
                            className="my-billing__link"
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`my-billing-pdf-${invoice.id}`}
                          >
                            PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/*
            Said once, under the table, rather than on every row that has no Pay button. An
            organization billed from here pays by cheque, wire or ACH against whatever its invoice
            says, and a customer looking for a Pay button deserves to be told why there is not one
            instead of concluding the page is broken.
          */}
          {!billing.isStripeManaged && billing.items.length > 0 && (
            <p className="my-billing__message" data-testid="my-billing-offline-note">
              These invoices are settled by cheque, wire or bank transfer using the details on the
              invoice. Contact us if you would rather pay by card.
            </p>
          )}
        </>
      )}
    </section>
  );
};
